/**
 * Mirror of `window`-level CustomEvent types dispatched by the commentary
 * React component. Keep field names in sync with commentary-ui's
 * `src/types/cross-shadow-events.ts` — the repos can't share types directly.
 */

export interface CommentaryThreadsChangedDetail {
  accountId: string
  /** Optional for forward compatibility; older React builds may omit it. */
  reason?:
    | 'thread-created'
    | 'comment-appended'
    | 'thread-updated'
    | 'comment-edited'
}

/**
 * DLITE-4369 — result of a host-requested single-thread deep link.
 * `'error'`/`'ignored'` were added on commentary-ui's side (found via MR
 * review) after helios's `commentary-deep-link-resolved` handling was
 * first built against the original three-value contract — keep this in
 * sync with commentary-ui's `DeepLinkResolvedDetail`
 * (src/features/comments-drawer/deep-link-events.ts).
 *
 * Found via MR review, second pass: this list is NOT closed at runtime —
 * see the `typeof d.result === 'string'` check below, not a fixed-set
 * membership test. commentary-ui ships as a runtime-loaded bundle, so a
 * newer commentary-ui can reach an already-deployed helios with no helios
 * release in between; a future sixth value must degrade like `reason?` on
 * `CommentaryThreadsChangedDetail` above (forward-compatible), not vanish
 * the way a closed allowlist would — that reintroduces the exact "host
 * waits forever" hang `'ignored'` exists to prevent. Consumers (see
 * app.component's onCommentaryDeepLinkResolved) must treat any result
 * outside the known failure cases (`'forbidden'`/`'not-found'`) as
 * "reveal, no toast" for the same reason.
 */
export type CommentaryDeepLinkResult =
  | 'opened'
  | 'forbidden'
  | 'not-found'
  | 'error'
  | 'ignored'

export interface CommentaryDeepLinkResolvedDetail {
  result: CommentaryDeepLinkResult
}

export interface CommentaryShadowEventMap {
  'commentary-threads-changed': CommentaryThreadsChangedDetail
  'commentary-deep-link-resolved': CommentaryDeepLinkResolvedDetail
}

export type CommentaryShadowEventName = keyof CommentaryShadowEventMap

/**
 * Narrows an `Event` to its typed detail, or returns `null` for malformed
 * dispatches so a stale React build can't crash helios listeners.
 */
export function readCommentaryShadowEvent<K extends CommentaryShadowEventName>(
  name: K,
  event: Event,
): CommentaryShadowEventMap[K] | null {
  if (!(event instanceof CustomEvent)) return null
  const detail = event.detail as
    | Partial<CommentaryShadowEventMap[K]>
    | undefined
  if (!detail) return null
  if (name === 'commentary-threads-changed') {
    const d = detail as Partial<CommentaryThreadsChangedDetail>
    if (!d.accountId) return null
  }
  if (name === 'commentary-deep-link-resolved') {
    const d = detail as Partial<CommentaryDeepLinkResolvedDetail>
    if (typeof d.result !== 'string' || d.result.length === 0) return null
  }
  return detail as CommentaryShadowEventMap[K]
}
